import React from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

export type StatTileTone = 'default' | 'accent' | 'warn';

export interface StatTileProps {
  value: number | string;
  label: string;
  /** Optional secondary line under the label (e.g. "2 overdue"). */
  sub?: string;
  /** Route to the lens this stat filters into — renders the tile as a Link. */
  to?: string;
  /** Renders the tile as a button when no `to` is given. */
  onClick?: () => void;
  tone?: StatTileTone;
  /** Hide entirely at zero — stat tiles are never filler (contract §7). */
  suppressAtZero?: boolean;
  className?: string;
  'data-testid'?: string;
}

const toneClasses: Record<StatTileTone, string> = {
  default: 'text-text-primary',
  accent: 'text-[var(--theme-primary)]',
  warn: 'text-[var(--theme-error)]',
};

const baseClasses =
  'flex flex-col items-start gap-0.5 bg-bg-card border border-border-light rounded-[14px] px-3 pt-3 pb-2.5 text-left';

const interactiveClasses =
  'cursor-pointer transition-colors hover:border-border-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent no-underline';

export function StatTile({
  value,
  label,
  sub,
  to,
  onClick,
  tone = 'default',
  suppressAtZero = false,
  className,
  'data-testid': testId,
}: StatTileProps) {
  if (suppressAtZero && (value === 0 || value === '0')) {
    return null;
  }

  const content = (
    <>
      <span className={twMerge('text-[22px] font-bold leading-none', toneClasses[tone])}>
        {value}
      </span>
      <span className="text-[10.5px] font-medium uppercase tracking-[.07em] text-text-tertiary">
        {label}
      </span>
      {sub && <span className="text-xs text-text-secondary">{sub}</span>}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={twMerge(baseClasses, interactiveClasses, className)} data-testid={testId}>
        {content}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={twMerge(baseClasses, interactiveClasses, className)}
        data-testid={testId}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={twMerge(baseClasses, className)} data-testid={testId}>
      {content}
    </div>
  );
}
